// @ts-check

import { useCallback, useEffect, useRef, useState } from "react"
import {
  getCategoriesOfTasksOfClientId,
  getIndividualTasksOfClientId,
  getModulesOfTasksOfClientId,
  getProjectsOfTasksOfClientId,
  getTeamMembersOfTasksOfClientId,
} from "../../../../../services/report.service"
import {
  formatCategoriesDropdownFilter,
  formatModulesDropdownFilter,
  formatProjectsDropdownFilter,
  formatTeamMembersDropdownFilter,
} from "../../../../../utils/report.utils"
import useToast from "../../../../../hooks/useToast.hook"
import { UNIFIEDDATE_VALUES } from "../../../../../consts/CompoundRangeDatePicker"

/** @param {import("./useClientTasksTableData.hook.types").UseClientTasksTableDataParams} params */
export function useClientTasksTableData(params) {
  const { clientId, dateRange, clientHasValidSprint, periodType } = params
  const [startDate, endDate] = dateRange || [null, null]

  const [categories, setCategories] = useState(
    /** @type {import("../../../../../utils/report.utils").FormattedCategory[]} */ ([])
  )
  const [modules, setModules] = useState(
    /** @type {import("../../../../../utils/report.utils").FormattedModule[]} */ ([])
  )
  const [projects, setProjects] = useState(
    /** @type {import("../../../../../utils/report.utils").ProjectGroup[]} */ ([])
  )
  const [teamMembers, setTeamMembers] = useState(
    /** @type {import("../../../../../utils/report.utils").TeamMemberGroup[]} */ ([])
  )

  const [tasks, setTasks] = useState(
    /** @type {import("../../../../../services/reports.service.types").GlobalTaskReportItem[]} */ ([])
  )

  const [isLoadingTasks, setIsLoadingTasks] = useState(false)
  const [isLoadingMeta, setIsLoadingMeta] = useState(false)

  const tasksRequestIdRef = useRef(0)
  const metaRequestIdRef = useRef(0)

  const { setErrorMessage } = useToast()

  const fetchTasks = useCallback(async () => {
    if (
      !clientId ||
      !startDate ||
      !endDate ||
      (!clientHasValidSprint && periodType === UNIFIEDDATE_VALUES.SPRINT)
    )
      return

    setIsLoadingTasks(true)

    const requestId = ++tasksRequestIdRef.current

    const response = await getIndividualTasksOfClientId({
      params: {
        clientId,
        startDate,
        endDate,
      },
    })

    if (tasksRequestIdRef.current !== requestId) return

    if (response.success && response.result) {
      setTasks(response.result.tasks)
    }

    setIsLoadingTasks(false)
  }, [clientId, startDate, endDate, clientHasValidSprint, periodType])

  const resetMeta = useCallback(() => {
    setCategories([])
    setModules([])
    setProjects([])
    setTeamMembers([])
  }, [])

  const fetchMeta = useCallback(async () => {
    if (
      !clientId ||
      !startDate ||
      !endDate ||
      (!clientHasValidSprint && periodType === UNIFIEDDATE_VALUES.SPRINT)
    )
      return

    setIsLoadingMeta(true)

    const requestId = ++metaRequestIdRef.current

    try {
      const [teamRes, catRes, modRes, projRes] = await Promise.all([
        getTeamMembersOfTasksOfClientId(clientId, startDate, endDate),
        getCategoriesOfTasksOfClientId(clientId, startDate, endDate),
        getModulesOfTasksOfClientId(clientId, startDate, endDate),
        getProjectsOfTasksOfClientId(clientId, startDate, endDate),
      ])

      if (metaRequestIdRef.current !== requestId) return

      setTeamMembers(formatTeamMembersDropdownFilter(teamRes.result))
      setCategories(formatCategoriesDropdownFilter(catRes.result, true))
      setModules(formatModulesDropdownFilter(modRes.result))
      setProjects(formatProjectsDropdownFilter(projRes.result))
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage({ message: error.message })
      }
    } finally {
      if (metaRequestIdRef.current === requestId) {
        setIsLoadingMeta(false)
      }
    }
  }, [clientId, startDate, endDate, clientHasValidSprint, periodType])

  const resetData = useCallback(() => {
    resetMeta()
    setTasks([])
  }, [resetMeta])

  const refreshData = useCallback(() => {
    resetData()
    fetchMeta()
    fetchTasks()
  }, [fetchMeta, fetchTasks, resetData])

  useEffect(() => {
    if (
      !clientId ||
      !startDate ||
      !endDate ||
      (!clientHasValidSprint && periodType === UNIFIEDDATE_VALUES.SPRINT)
    ) {
      resetData()
      return
    }

    resetData()
    fetchMeta()
    fetchTasks()
  }, [clientId, startDate, endDate, clientHasValidSprint, periodType])

  return {
    tasks,
    categories,
    modules,
    projects,
    teamMembers,
    isLoading: isLoadingTasks || isLoadingMeta,
    refreshData,
    resetData,
  }
}
